import { httpClient } from '@/shared/api';
import {
  toCalcBandSchoolCountPostBody,
  type CalcBandSchoolCountItem,
  type SchoolByIdsDto,
} from './schoolBands.dto';
import { mapSchoolByIdsItem } from './schoolBands.api';
import type {
  ParentPopulateBandMembershipResult,
  ParentTuitionBandDto,
  ParentTuitionBandSaveInput,
  ParentTuitionBandsPagedResult,
} from './parentTuitionBand.dto';

const PREFIX = '/app/parent-tuition-band';

function readParentTuitionBandEntityId(o: Record<string, unknown>): string | undefined {
  const keys = [
    'id',
    'Id',
    'entityId',
    'EntityId',
    'parentTuitionBandId',
    'ParentTuitionBandId',
  ] as const;
  for (const k of keys) {
    const v = o[k];
    if (v == null || v === '') continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return undefined;
}

function mapItem(raw: unknown): ParentTuitionBandDto {
  if (!raw || typeof raw !== 'object') {
    return {
      name: '',
      minValue: 0,
      maxValue: 0,
      minSchoolsPerBand: 0,
      year: 0,
      numberOfSchools: 0,
    };
  }
  const o = raw as Record<string, unknown>;
  const sid = o.schoolIds ?? o.SchoolIds;
  let schoolIds: Record<string, number> | undefined;
  if (sid && typeof sid === 'object' && !Array.isArray(sid)) {
    schoolIds = {};
    for (const [k, v] of Object.entries(sid as Record<string, unknown>)) {
      const n = typeof v === 'number' ? v : Number(v);
      if (Number.isFinite(n)) schoolIds[k] = n;
    }
  }
  return {
    id: readParentTuitionBandEntityId(o),
    name: String(o.name ?? o.Name ?? ''),
    fullName: (o.fullName ?? o.FullName) as string | null | undefined,
    minValue: Number(o.minValue ?? o.MinValue ?? 0),
    maxValue: Number(o.maxValue ?? o.MaxValue ?? 0),
    description: (o.description ?? o.Description) as string | null | undefined,
    minSchoolsPerBand: Number(o.minSchoolsPerBand ?? o.MinSchoolsPerBand ?? 0),
    year: Number(o.year ?? o.Year ?? 0),
    numberOfSchools: Number(o.numberOfSchools ?? o.NumberOfSchools ?? 0),
    totalNumberOfSchools: (() => {
      const v = o.totalNumberOfSchools ?? o.TotalNumberOfSchools;
      return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
    })(),
    totalNumberOfSchoolsOutsideBands: (() => {
      const v =
        o.totalNumberOfSchoolsOutsideBands ??
        o.TotalNumberOfSchoolsOutsideBands ??
        o.totalNumberOfSchoolsOtsideBands ??
        o.TotalNumberOfSchoolsOtsideBands;
      return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
    })(),
    schoolIds,
    schools: (() => {
      const rawSchools = o.schools ?? o.Schools;
      if (!Array.isArray(rawSchools)) return undefined;
      const schools = rawSchools
        .map(mapSchoolByIdsItem)
        .filter((s): s is SchoolByIdsDto => Boolean(s.id?.trim()));
      return schools.length ? schools : undefined;
    })(),
  };
}

function normalizePaged(raw: unknown): ParentTuitionBandsPagedResult {
  if (!raw || typeof raw !== 'object') {
    return { items: [], totalCount: 0 };
  }
  const o = raw as Record<string, unknown>;
  const rawItems = (o.items ?? o.Items) as unknown[] | undefined;
  const items = Array.isArray(rawItems) ? rawItems.map(mapItem) : [];
  const tc = o.totalCount ?? o.TotalCount;
  const totalCount = typeof tc === 'number' && Number.isFinite(tc) ? tc : items.length;
  return { items, totalCount };
}

function toQuery(params: {
  Year: number;
  MinSchoolsPerBand?: number;
  Name?: string;
  SkipCount?: number;
  MaxResultCount?: number;
  Sorting?: string;
  Dir?: string;
  SchoolIds?: string[];
}): string {
  const search = new URLSearchParams();
  search.set('Year', String(params.Year));
  if (params.MinSchoolsPerBand != null) {
    search.set('MinSchoolsPerBand', String(params.MinSchoolsPerBand));
  }
  if (params.Name != null && params.Name !== '') search.set('Name', params.Name);
  if (params.SkipCount != null) search.set('SkipCount', String(params.SkipCount));
  if (params.MaxResultCount != null) search.set('MaxResultCount', String(params.MaxResultCount));
  if (params.Sorting != null && params.Sorting !== '') search.set('Sorting', params.Sorting);
  if (params.Dir != null && params.Dir !== '') search.set('dir', params.Dir);
  if (params.SchoolIds?.length) {
    for (const id of params.SchoolIds) {
      if (id?.trim()) search.append('SchoolIds', id.trim());
    }
  }
  const q = search.toString();
  return q ? `?${q}` : '';
}

function normalizePopulateBandMembershipResponse(raw: unknown): ParentPopulateBandMembershipResult {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    const rawBands = (o.bands ?? o.Bands) as unknown[] | undefined;
    const bands = Array.isArray(rawBands) ? rawBands.map(mapItem) : [];
    const tnsRaw = o.totalNumberOfSchools ?? o.TotalNumberOfSchools;
    const tnsobRaw =
      o.totalNumberOfSchoolsOutsideBands ??
      o.TotalNumberOfSchoolsOutsideBands ??
      o.totalNumberOfSchoolsOtsideBands ??
      o.TotalNumberOfSchoolsOtsideBands;
    const rawSchools = (o.schools ?? o.Schools) as unknown[] | undefined;
    const schoolsOutsideBands = Array.isArray(rawSchools)
      ? rawSchools.map(mapSchoolByIdsItem).filter((s) => Boolean(s.id?.trim()))
      : [];
    return {
      bands,
      totalNumberOfSchools:
        typeof tnsRaw === 'number' && Number.isFinite(tnsRaw) ? tnsRaw : undefined,
      totalNumberOfSchoolsOutsideBands:
        typeof tnsobRaw === 'number' && Number.isFinite(tnsobRaw) ? tnsobRaw : undefined,
      schoolsOutsideBands,
    };
  }
  if (Array.isArray(raw)) {
    const bands = raw.map(mapItem);
    return { bands, schoolsOutsideBands: [] };
  }
  return { bands: [], schoolsOutsideBands: [] };
}

export const parentTuitionBandApi = {
  /**
   * POST /api/app/parent-tuition-band/populate-band-membership?targetYear=…
   * Refreshes `numberOfSchools`, `schoolIds`, and universe totals for the given draft band rows.
   */
  populateBandMembership: async (
    targetYear: number,
    items: CalcBandSchoolCountItem[]
  ): Promise<ParentPopulateBandMembershipResult> => {
    const q = new URLSearchParams();
    q.set('targetYear', String(targetYear));
    const raw = await httpClient.post<unknown>(
      `${PREFIX}/populate-band-membership?${q.toString()}`,
      toCalcBandSchoolCountPostBody(items)
    );
    return normalizePopulateBandMembershipResponse(raw);
  },

  getList: async (params: {
    year: number;
    minSchoolsPerBand?: number;
    name?: string;
    schoolIds?: string[];
    dir?: string;
    sorting?: string;
    skipCount?: number;
    maxResultCount?: number;
  }): Promise<ParentTuitionBandsPagedResult> => {
    const raw = await httpClient.get<unknown>(
      `${PREFIX}${toQuery({
        Year: params.year,
        MinSchoolsPerBand: params.minSchoolsPerBand,
        Name: params.name,
        SkipCount: params.skipCount ?? 0,
        MaxResultCount: params.maxResultCount ?? 100,
        Sorting: params.sorting,
        Dir: params.dir,
        SchoolIds: params.schoolIds,
      })}`
    );
    return normalizePaged(raw);
  },

  /**
   * POST /api/app/parent-tuition-band — create (ABP application service convention).
   */
  create: async (body: ParentTuitionBandSaveInput): Promise<ParentTuitionBandDto> => {
    const raw = await httpClient.post<unknown>(`${PREFIX}`, body);
    return mapItem(raw);
  },

  /**
   * PUT /api/app/parent-tuition-band/{id} — update.
   */
  update: async (id: string, body: ParentTuitionBandSaveInput): Promise<ParentTuitionBandDto> => {
    const raw = await httpClient.put<unknown>(`${PREFIX}/${encodeURIComponent(id)}`, body);
    return mapItem(raw);
  },

  /**
   * DELETE /api/app/parent-tuition-band/{id} — delete.
   */
  delete: async (id: string): Promise<void> => {
    await httpClient.delete(`${PREFIX}/${encodeURIComponent(id)}`);
  },
};
